import { ClientReservation, ReservationStatus } from '@src/@data/types/clients';
import moment from 'moment-timezone';

export interface ClientReservationDisplayData {
  formattedDate: string;
  formattedTime: string;
  isToday: boolean;
  isUpcoming: boolean;
  statusLabelKey: string;
  statusColor: string;
}

const resolveStatusColor = (status: ReservationStatus) => {
  switch (status) {
    case ReservationStatus.Pending:
    case ReservationStatus.PendingPayment:
    case ReservationStatus.AwaitingPayment:
      return 'light-warning';
    case ReservationStatus.Confirmed:
    case ReservationStatus.Paid:
    case ReservationStatus.Finished:
    case ReservationStatus.Reviewed:
      return 'light-success';
    case ReservationStatus.Rejected:
    case ReservationStatus.Cancelled:
    case ReservationStatus.FailedPayment:
    case ReservationStatus.Missed:
      return 'light-danger';
  }
};

const resolveStatusLabelKey = (status: ReservationStatus) => {
  switch (status) {
    case ReservationStatus.Pending:
      return 'reservation_state_pending';
    case ReservationStatus.PendingPayment:
      return 'reservation_state_pending_payment';
    case ReservationStatus.Confirmed:
      return 'reservation_state_confirmed';
    case ReservationStatus.Paid:
      return 'reservation_state_paid';
    case ReservationStatus.Finished:
      return 'reservation_state_finished';
    case ReservationStatus.Reviewed:
      return 'reservation_state_reviewed';
    case ReservationStatus.Rejected:
      return 'reservation_state_rejected';
    case ReservationStatus.Cancelled:
      return 'reservation_state_cancelled';
    case ReservationStatus.FailedPayment:
      return 'reservation_state_failed_payment';
    case ReservationStatus.Missed:
      return 'reservation_state_missed';
    case ReservationStatus.AwaitingPayment:
      return 'reservation_state_awaiting_payment';
  }
};

export const getReservationDisplayData = (
  reservation: Pick<ClientReservation, 'startDate' | 'timeZone' | 'endDate'> &
    Partial<Pick<ClientReservation, 'status'>>
): ClientReservationDisplayData => {
  const start = moment.tz(reservation.startDate, reservation.timeZone);
  const end = moment.tz(reservation.endDate, reservation.timeZone);
  const now = moment.tz(reservation.timeZone);

  const startTime = start.format('HH:mm');
  const endTime = end.format('HH:mm');

  const formattedDate = start.format('LL');
  const formattedTime = `${startTime} — ${endTime}`;
  const isToday = start.isSame(now, 'days');
  const isUpcoming = start.isBefore(now);
  const statusLabelKey = reservation.status
    ? resolveStatusLabelKey(reservation.status)
    : '';
  const statusColor = reservation.status
    ? resolveStatusColor(reservation.status)
    : '';

  return {
    formattedDate,
    formattedTime,
    isToday,
    isUpcoming,
    statusLabelKey,
    statusColor,
  };
};
